import type {
  CreateGatewayKeyResponse,
  CreateWorkspaceResponse,
  GatewayKeyInfo,
  LogsResponse,
  MeResponse,
  RunDetail,
  RunStatus,
  RunsPage,
  SkillSummary,
  SpendCapInfo,
  SystemStatus,
  UserInfo,
  WorkspaceInfo,
  WorkspaceInviteInfo,
  WorkspaceMemberInfo,
} from '../types/api'
import { api } from './client'

export const meApi = {
  get: () => api.get<MeResponse>('/api/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.patch<void>('/api/me', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  updateEmail: (email: string) => api.patch<void>('/api/me', { email }),
}

export const workspacesApi = {
  list: () => api.get<WorkspaceInfo[]>('/api/workspaces'),
  create: (name: string) =>
    api.post<CreateWorkspaceResponse>('/api/workspaces', { name }),
  get: (workspaceId: string) =>
    api.get<WorkspaceInfo>(`/api/workspaces/${workspaceId}`),
  rename: (workspaceId: string, name: string) =>
    api.patch<void>(`/api/workspaces/${workspaceId}`, { name }),
  delete: (workspaceId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}`),
  deleteWithMeta: (workspaceId: string) =>
    api.deleteWithMeta<void>(`/api/workspaces/${workspaceId}`),

  listMembers: (workspaceId: string) =>
    api.get<WorkspaceMemberInfo[]>(`/api/workspaces/${workspaceId}/members`),
  addMember: (workspaceId: string, username: string, role: string) =>
    api.post<void>(`/api/workspaces/${workspaceId}/members`, {
      username,
      role,
    }),
  updateMember: (
    workspaceId: string,
    userId: string,
    role: string,
    opts?: { transfer?: boolean },
  ) =>
    api.patch<void>(`/api/workspaces/${workspaceId}/members/${userId}`, {
      role,
      ...(opts?.transfer ? { transfer: true } : {}),
    }),
  transferOwnership: (workspaceId: string, userId: string) =>
    api.patch<void>(`/api/workspaces/${workspaceId}/members/${userId}`, {
      role: 'owner',
      transfer: true,
    }),
  removeMember: (workspaceId: string, userId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}/members/${userId}`),

  getSpendCap: (workspaceId: string) =>
    api.get<SpendCapInfo>(`/api/workspaces/${workspaceId}/spend-cap`),
  setSpendCap: (workspaceId: string, capUsd: number | null) =>
    api.put<void>(`/api/workspaces/${workspaceId}/spend-cap`, {
      cap_usd: capUsd,
    }),

  listInvites: (workspaceId: string) =>
    api.get<WorkspaceInviteInfo[]>(`/api/workspaces/${workspaceId}/invites`),
  sendInvite: (workspaceId: string, email: string, role: string) =>
    api.post<void>(`/api/workspaces/${workspaceId}/invites`, { email, role }),
  revokeInvite: (workspaceId: string, inviteId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}/invites/${inviteId}`),

  listSkills: (workspaceId: string) =>
    api.get<SkillSummary[]>(`/api/workspaces/${workspaceId}/skills`),
}

export const gatewayKeysApi = {
  list: (workspaceId: string) =>
    api.get<GatewayKeyInfo[]>(`/api/workspaces/${workspaceId}/gateway/keys`),
  create: (
    workspaceId: string,
    name: string,
    opts?: { topic_id?: string; provider_id?: string; model_override?: string },
  ) =>
    api.post<CreateGatewayKeyResponse>(
      `/api/workspaces/${workspaceId}/gateway/keys`,
      { name, ...opts },
    ),
  revoke: (workspaceId: string, keyId: string) =>
    api.delete<void>(`/api/workspaces/${workspaceId}/gateway/keys/${keyId}`),
}

export const adminApi = {
  listUsers: () => api.get<UserInfo[]>('/api/users'),
  createUser: (username: string, password: string) =>
    api.post<UserInfo>('/api/users', { username, password }),
  deleteUser: (userId: string) => api.delete<void>(`/api/users/${userId}`),
  updateUser: (
    userId: string,
    updates: { is_system_admin?: boolean; password?: string },
  ) => api.patch<void>(`/api/users/${userId}`, updates),
  listAllWorkspaces: () => api.get<WorkspaceInfo[]>('/api/admin/workspaces'),
  systemStatus: () => api.get<SystemStatus>('/api/admin/status'),
  recentLogs: (opts?: {
    limit?: number
    level?: string
    request_id?: string
    q?: string
    workspace_id?: string
  }) => {
    const params = new URLSearchParams()
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.level) params.set('level', opts.level)
    if (opts?.request_id) params.set('request_id', opts.request_id)
    if (opts?.q) params.set('q', opts.q)
    if (opts?.workspace_id) params.set('workspace_id', opts.workspace_id)
    const qs = params.toString()
    return api.get<LogsResponse>(`/api/admin/logs${qs ? `?${qs}` : ''}`)
  },
}

interface RunsListOpts {
  status?: RunStatus
  limit?: number
  before?: string
}

function runsQuery(opts?: RunsListOpts): string {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.before) params.set('before', opts.before)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const runsApi = {
  listWorkspaceRuns: (workspaceId: string, opts?: RunsListOpts) =>
    api.get<RunsPage>(`/api/workspaces/${workspaceId}/runs${runsQuery(opts)}`),
  getRun: (workspaceId: string, runId: string) =>
    api.get<RunDetail>(`/api/workspaces/${workspaceId}/runs/${runId}`),
}
